import {Download, Info, Video} from 'lucide-react'
import {AppHeader} from '@/widgets/app-header'
import {Button} from '@/shared/ui'
import {cn, getRecordingDownloadUrl, getRecordingsList} from '@/shared/lib'
import type {StoredRecording} from '@/entities/recording'

function formatDate(iso: string): string {
  const date = new Date(iso)
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function RecordingsPage() {
  const recordings: StoredRecording[] = getRecordingsList()

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden">
      <AppHeader />
      <main className="relative flex flex-1 flex-col px-4 py-8 sm:px-8">
        <div className="mx-auto w-full max-w-3xl animate-in fade-in slide-in-from-bottom-3 duration-500">
          <h1 className="mb-6 text-2xl font-semibold tracking-tight">Записи созвонов</h1>

          <div
            className={cn(
              'mb-6 flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm text-muted-foreground',
            )}
          >
            <Info className="mt-0.5 size-4 shrink-0 text-primary" />
            <p>
              После окончания созвона запись может обрабатываться некоторое время перед тем, как
              станет доступна для скачивания. Если скачивание не начинается сразу — подождите
              немного и попробуйте снова.
            </p>
          </div>

          {recordings.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 py-24 text-muted-foreground">
              <Video className="size-12 opacity-30" />
              <p className="text-sm">Записей пока нет</p>
              <p className="max-w-xs text-center text-xs opacity-70">
                Записи созвонов появятся здесь после того, как вы нажмёте кнопку записи в комнате.
              </p>
            </div>
          ) : (
            <div
              className={cn(
                'overflow-hidden rounded-[16px] border border-border/60 bg-card/80',
                'backdrop-blur-[20px] shadow-(--shadow-card)',
              )}
            >
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 text-left text-xs text-muted-foreground">
                    <th className="px-5 py-3 font-medium">Дата</th>
                    <th className="px-5 py-3 font-medium">Комната</th>
                    <th className="px-5 py-3 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {recordings.map((rec, idx) => (
                    <tr
                      key={rec.call_id}
                      className={cn(
                        'transition-colors duration-150 hover:bg-muted/30',
                        idx !== recordings.length - 1 && 'border-b border-border/40',
                      )}
                    >
                      <td className="px-5 py-3.5 tabular-nums text-foreground">
                        {formatDate(rec.started_at)}
                      </td>
                      <td className="max-w-[200px] truncate px-5 py-3.5 font-mono text-xs text-muted-foreground">
                        {rec.room_id}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <Button variant="outline" size="sm" asChild>
                          <a
                            href={getRecordingDownloadUrl(rec.call_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Download className="size-3.5" />
                            Скачать
                          </a>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
